import { Directive, TemplateRef, inject, input, Signal, signal } from '@angular/core';

/**
 * Structural directive to declare a wizard step on an `<ng-template>`.
 *
 * Usage:
 * ```html
 * <app-wizard-shell>
 *   <ng-template appWizardStep="source"
 *                stepLabel="Data Source"
 *                stepIcon="cloud"
 *                [stepValid]="sourceValid">
 *     <app-data-source-step />
 *   </ng-template>
 * </app-wizard-shell>
 * ```
 */
@Directive({ selector: '[appWizardStep]' })
export class WizardStepDirective {
  /** Unique identifier for this step. */
  readonly stepId = input.required<string>({ alias: 'appWizardStep' });

  /** Human-readable label shown in the step bar. */
  readonly stepLabel = input.required<string>();

  /** Optional Material Icon name. */
  readonly stepIcon = input<string>();

  /** Signal indicating whether this step's form is valid. Defaults to always-true. */
  readonly stepValid = input<Signal<boolean>>(signal(true));

  /**
   * Signal controlling visibility of this step.
   * Hidden steps are excluded from the bar and skipped during navigation.
   * Defaults to always-visible.
   */
  readonly stepVisible = input<Signal<boolean>>(signal(true));

  readonly templateRef = inject<TemplateRef<unknown>>(TemplateRef);
}
